/**
 * @file StatementDeliverModal.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-04
 * @modified 2026-10-19
 *
 * Regenerate / preview / redeliver a single statement's documents. Reused by the
 * contact-modal Statements panel and the admin statement-run-history component.
 * Lets the user choose WHAT (statement / invoices / both), which TEMPLATE
 * (email letterhead vs print), then PREVIEW or REDELIVER via a channel
 * (email customer, email a typed address, print, or save/download).
 * Backed by POST /api/statements/:id/preview-pdf (the preview, drawn in the
 * app by StatementPreviewModal — it never opens a browser tab, Cam 19 Oct 2026)
 * and POST /api/statements/:id/redeliver.
 */
import React, { useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import SenderSelect from './SenderSelect';
import StatementPreviewModal from './StatementPreviewModal';
import './StatementDeliverModal.css';

function StatementDeliverModal({ statement, user, onClose, onDelivered }) {
  const [include, setInclude] = useState('both');     // both | statement | invoices
  const [template, setTemplate] = useState('email');  // email | print
  const [emailAddress, setEmailAddress] = useState('');
  const [busy, setBusy] = useState('');               // which action is in-flight
  const [message, setMessage] = useState(null);       // { type, text }
  // Sender for the email channels. Customer statements default to the shared
  // accounts@ finance mailbox; switchable to the acting user or another mailbox.
  const [sendAs, setSendAs] = useState('accounts');
  // The preview being shown (what and which template, as chosen when opened).
  const [preview, setPreview] = useState(null);

  if (!statement) return null;
  const sid = statement.id;
  // A contact with no account number has only a name: no badge (it used to
  // read "null").
  const accno = statement.account_no ? String(statement.account_no) : '';
  const accname = statement.company_name || '';
  const period = statement.period_start ? `${statement.period_start} → ${statement.period_end}` : '';

  const openPreview = () => setPreview({ include, template });

  const redeliver = async (channel) => {
    setMessage(null);
    if (channel === 'email_address' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress)) {
      setMessage({ type: 'error', text: 'Enter a valid email address.' });
      return;
    }
    setBusy(channel);
    try {
      const res = await fetch(`${API_URL}/api/statements/${sid}/redeliver`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ include, template, channel, email: channel === 'email_address' ? emailAddress : undefined, sendAs })
      });
      if (channel === 'save') {
        if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `Failed (${res.status})`); }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // The server's name (account number, else company name — the same as
        // the batch save gives this statement); this fallback only if absent.
        const named = (res.headers.get('Content-Disposition') || '').match(/filename="?([^"]+)"?/);
        a.download = named ? named[1] : `${include === 'invoices' ? 'Invoices' : 'Statement'}_${statement.account_no || sid}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        setMessage({ type: 'success', text: 'Downloaded.' });
      } else {
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `Failed (${res.status})`);
        setMessage({ type: 'success', text: data.message || 'Done.' });
      }
      if (onDelivered) onDelivered();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBusy('');
    }
  };

  const seg = (val, cur, set, text) => (
    <button type="button" className={`sdm-seg ${cur === val ? 'active' : ''}`} onClick={() => set(val)}>{text}</button>
  );

  return (
    <>
      {/* Escape closes the preview, not this window under it. */}
      <Modal isOpen={true} onClose={onClose} title="Regenerate / Redeliver" className="statement-deliver" closeOnEscape={!preview}>
        <div className="sdm-body">
          <div className="sdm-target">
            <span className="sdm-contact">{accno && <span className="accno">{accno}</span>}{accname}</span>
            {period && <span className="sdm-period"><strong>Statement Period: </strong>{period}</span>}
          </div>

          <label className="sdm-label">Include</label>
          <div className="sdm-segs">
            {seg('both', include, setInclude, 'Statement and invoices')}
            {seg('statement', include, setInclude, 'Statement only')}
            {seg('invoices', include, setInclude, 'Invoices only')}
          </div>

          <label className="sdm-label">Template</label>
          <div className="sdm-segs">
            {seg('email', template, setTemplate, 'For Email/ Screens')}
            {seg('print', template, setTemplate, 'For Print')}
          </div>

          <label className="sdm-label">Send from <span className="sdm-hint">(email only)</span></label>
          <SenderSelect user={user} value={sendAs} onChange={setSendAs} disabled={!!busy} />

          <label className="sdm-label">Actions</label>
          <div className="sdm-actions">
            <button type="button" className="sdm-btn" onClick={openPreview}>
              <span className="material-symbols-rounded">visibility</span> Preview
            </button>
            <button type="button" className="sdm-btn" disabled={busy} onClick={() => redeliver('save')}>
              <span className="material-symbols-rounded">download</span> {busy === 'save' ? '…' : 'Save / Download'}
            </button>
            <button type="button" className="sdm-btn" disabled={busy} onClick={() => redeliver('print')}>
              <span className="material-symbols-rounded">print</span> {busy === 'print' ? '…' : 'Print'}
            </button>
            <button type="button" className="sdm-btn" disabled={busy} onClick={() => redeliver('email_customer')}
              title={statement.customer_email ? `Email ${statement.customer_email}` : 'Email the customer’s address on file'}>
              <span className="material-symbols-rounded">mail</span> {busy === 'email_customer' ? '…' : 'Email to Customer'}
            </button>
          </div>

          <div className="sdm-email-row">
            <input type="email" placeholder="Enter alternative email address…" value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)} className="sdm-input" />
            <button type="button" className="sdm-btn" disabled={busy} onClick={() => redeliver('email_address')}>
              <span className="material-symbols-rounded">send</span> {busy === 'email_address' ? '…' : 'Send to Email'}
            </button>
          </div>

          {message && <div className={`sdm-msg ${message.type}`}>{message.text}</div>}
          <p className="sdm-note"><span className="material-symbols-rounded" style={{ fontSize: '16px', color: 'var(--color-primary)' }}>info</span>Note: Regeneration uses the statement's original stored figures (not live data); its customer note prints as it is now.</p>
        </div>
      </Modal>
      {preview && (
        <StatementPreviewModal
          statementId={sid}
          include={preview.include}
          template={preview.template}
          title={`Statement preview${accname ? ` — ${accname}` : ''}`}
          onClose={() => setPreview(null)}
        />
      )}
    </>
  );
}

export default StatementDeliverModal;
